import {
  BulkImportWizardColumnMapping,
  BulkImportWizardFieldDefinition,
  BulkImportWizardSheetMapping,
  ImportEntityType
} from '@betterboards/shared/types/Import'
import { getFieldDefinitions, EntityTypeDefinitions, UserTypeCandidates, CommitteeTagCandidates } from '@betterboards/shared/helpers/import'
import { normalizeString, fuzzyMatch } from '@betterboards/shared/helpers/fuzzyMatch'
import { transformToEntity } from '@betterboards/shared/helpers/import/transformers'
import { UserType, CommitteeTag } from '@betterboards/shared/types/API'
import normalizeDateToISO from '@betterboards/shared/helpers/util/normalizeDateToISO'

const DATE_FIELDS = ['joinDate', 'dateOfBirth']

/**
 * Infer committee type from the committee name using fuzzy matching
 * E.g., "Remuneration Committee" → CommitteeTag.Remuneration
 */
export function committeeTypeFromName (committeeName: string): CommitteeTag {
  const inferredType = fuzzyMatch(committeeName, CommitteeTagCandidates)
  return inferredType ?? CommitteeTag.Other
}

/**
 * Auto-detect entity type from a sheet name using fuzzy matching
 */
export function detectEntityTypeFromSheetName (
  sheetName: string,
  fuzzyThreshold: number = 0.7
): ImportEntityType | null {
  const normalizedSheetName = normalizeString(sheetName)

  // Check for substring matches first (e.g., "Company Import" contains "company")
  for (const definition of EntityTypeDefinitions) {
    const allMatchStrings = [definition.type, ...definition.aliases]
    for (const matchString of allMatchStrings) {
      const normalizedMatch = normalizeString(matchString)
      if (normalizedSheetName.includes(normalizedMatch) || normalizedMatch.includes(normalizedSheetName)) {
        return definition.type
      }
    }
  }

  // Fall back to fuzzy matching
  const candidates = EntityTypeDefinitions.map(def => ({
    result: def.type,
    matchStrings: [def.type, ...def.aliases]
  }))
  return fuzzyMatch(sheetName, candidates, fuzzyThreshold)
}

/**
 * Find the best matching field definition for a given column name
 * Optionally exclude fields that are already mapped
 */
export function findBestMatch (
  columnName: string,
  fieldDefinitions: BulkImportWizardFieldDefinition[],
  fuzzyThreshold: number = 0.7,
  excludeKeys?: Set<string>
): BulkImportWizardFieldDefinition | null {
  const availableFields = excludeKeys
    ? fieldDefinitions.filter(field => !excludeKeys.has(field.key))
    : fieldDefinitions

  const candidates = availableFields.map(field => ({
    result: field,
    matchStrings: [field.key, field.label, ...field.aliases]
  }))
  return fuzzyMatch(columnName, candidates, fuzzyThreshold)
}

/**
 * Auto-map columns from a spreadsheet to entity field definitions
 * When the best match is already used, tries to find the next best match
 */
export function autoMapColumns (
  columns: string[],
  entityType: ImportEntityType,
  fuzzyThreshold: number = 0.7
): BulkImportWizardColumnMapping[] {
  const fieldDefinitions = getFieldDefinitions(entityType)
  const mappings: BulkImportWizardColumnMapping[] = []
  const usedFields = new Set<string>()

  for (const column of columns) {
    const match = findBestMatch(column, fieldDefinitions, fuzzyThreshold, usedFields)

    if (match) {
      usedFields.add(match.key)
      mappings.push({
        sourceColumn: column,
        targetField: match.key
      })
    } else {
      mappings.push({
        sourceColumn: column,
        targetField: null
      })
    }
  }

  return mappings
}

/**
 * Get unmapped required fields that need attention
 */
export function getUnmappedRequiredFields (
  mappings: BulkImportWizardColumnMapping[],
  entityType: ImportEntityType
): BulkImportWizardFieldDefinition[] {
  const fieldDefinitions = getFieldDefinitions(entityType)
  const mappedFields = new Set(
    mappings
      .filter(m => m.targetField !== null)
      .map(m => m.targetField)
  )

  return fieldDefinitions.filter(
    field => field.required && !mappedFields.has(field.key)
  )
}

/**
 * Get available (unmapped) fields for manual selection
 */
export function getAvailableFields (
  mappings: BulkImportWizardColumnMapping[],
  entityType: ImportEntityType
): BulkImportWizardFieldDefinition[] {
  const fieldDefinitions = getFieldDefinitions(entityType)
  const mappedFields = new Set(
    mappings
      .filter(m => m.targetField !== null)
      .map(m => m.targetField)
  )

  return fieldDefinitions.filter(field => !mappedFields.has(field.key))
}

/**
 * Clear a target field from all mappings except the given source column.
 * Used when the user selects a field already mapped elsewhere (steal on select).
 */
export function clearTargetFieldFromOtherMappings (
  mappings: BulkImportWizardColumnMapping[],
  targetField: string,
  sourceColumn: string
): void {
  for (const mapping of mappings) {
    if (mapping.sourceColumn !== sourceColumn && mapping.targetField === targetField) {
      mapping.targetField = null
    }
  }
}

/**
 * Parse a comma-separated string into an array of trimmed values
 */
export function parseCommaSeparatedList (value: unknown): string[] {
  if (typeof value !== 'string') {
    return []
  }
  return value
    .split(',')
    .map(item => item.trim())
    .filter(item => item.length > 0)
}

/**
 * Set a nested field value using dot notation (e.g., "address.city")
 */
function setNestedField (
  result: Record<string, unknown>,
  targetField: string,
  value: unknown
): void {
  const [parent, child] = targetField.split('.')
  if (!result[parent]) {
    result[parent] = {}
  }
  (result[parent] as Record<string, unknown>)[child] = value
}

/**
 * Apply field mappings to transform raw row data into mapped data.
 * This handles the column-to-field mapping only.
 */
export function mapFields (
  rowData: Record<string, unknown>,
  mappings: BulkImportWizardColumnMapping[]
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const mapping of mappings) {
    if (!mapping.targetField) {
      continue
    }

    const value = rowData[mapping.sourceColumn]
    if (value === undefined || value === null || value === '') {
      continue
    }

    const targetField = mapping.targetField

    switch (true) {
      // Handle nested fields like "address.city"
      case targetField.includes('.'):
        setNestedField(result, targetField, value)
        break

      // Parse comma-separated member lists into arrays
      case targetField === 'members':
        result[targetField] = parseCommaSeparatedList(value)
        break

      // Normalize date fields to ISO format (YYYY-MM-DD)
      case DATE_FIELDS.includes(targetField):
        result[targetField] = normalizeDateToISO(String(value))
        break

      // Pass through all other fields unchanged
      default:
        result[targetField] = value
    }
  }

  return result
}

/**
 * Apply field mappings and entity-specific transformations.
 * Combines mapping with entity transformation for complete data processing.
 */
export function applyMappings (
  rowData: Record<string, unknown>,
  mappings: BulkImportWizardColumnMapping[],
  entityType?: ImportEntityType
): Record<string, unknown> {
  // First, apply field mappings
  const mapped = mapFields(rowData, mappings)

  // Then apply entity-specific transformations if entity type is provided
  if (!entityType) {
    return mapped
  }

  // Merge mapped data with original row data for transformer access to unmapped fields
  const mergedData = { ...rowData, ...mapped }
  return transformToEntity(mergedData, entityType)
}

/**
 * Get a nested field value using dot notation (e.g., "address.city")
 */
function getNestedField (
  entityData: Record<string, unknown>,
  targetField: string
): unknown {
  const [parent, child] = targetField.split('.')
  const parentObj = entityData[parent]
  if (parentObj && typeof parentObj === 'object') {
    return (parentObj as Record<string, unknown>)[child]
  }
  return undefined
}

/**
 * Reverse field mappings to transform entity data back to raw row data
 */
export function reverseApplyMappings (
  entityData: Record<string, unknown>,
  mappings: BulkImportWizardColumnMapping[]
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const mapping of mappings) {
    if (!mapping.targetField || !mapping.sourceColumn) {
      continue
    }

    const targetField = mapping.targetField
    let value: unknown

    switch (true) {
      // Handle nested fields like "address.city"
      case targetField.includes('.'):
        value = getNestedField(entityData, targetField)
        break

      // Convert member arrays back to comma-separated strings
      case targetField === 'members' && Array.isArray(entityData[targetField]):
        value = (entityData[targetField] as string[]).join(', ')
        break

      // Pass through all other fields unchanged
      default:
        value = entityData[targetField]
    }

    if (value !== undefined && value !== null && value !== '') {
      result[mapping.sourceColumn] = value
    }
  }

  return result
}

/**
 * Validate that all required fields are mapped
 */
export function validateMappings (
  mappings: BulkImportWizardColumnMapping[],
  entityType: ImportEntityType
): { valid: boolean, missingRequired: string[] } {
  const unmappedRequired = getUnmappedRequiredFields(mappings, entityType)

  return {
    valid: unmappedRequired.length === 0,
    missingRequired: unmappedRequired.map(f => f.label)
  }
}

export interface MemberResolutionResult {
  resolvedIds: string[]
  unresolvedNames: string[]
}

/**
 * Resolve member names to Individual IDs by matching preferred names
 */
export function resolveMemberNames (
  memberNames: string[],
  individuals: Array<{ id: string, preferredName: string }>
): MemberResolutionResult {
  const resolvedIds: string[] = []
  const unresolvedNames: string[] = []

  for (const name of memberNames) {
    const normalizedName = normalizeString(name)
    const match = individuals.find(
      ind => normalizeString(ind.preferredName) === normalizedName
    )

    if (match) {
      resolvedIds.push(match.id)
    } else {
      unresolvedNames.push(name)
    }
  }

  return { resolvedIds, unresolvedNames }
}

/**
 * Match input string to UserType enum with fuzzy matching
 */
export function matchUserType (input: string): UserType | null {
  return fuzzyMatch(input, UserTypeCandidates)
}

/**
 * Match input string to CommitteeTag enum with fuzzy matching
 */
export function matchCommitteeTag (input: string): CommitteeTag | null {
  return fuzzyMatch(input, CommitteeTagCandidates) ?? CommitteeTag.Other
}

export function cloneBulkImportWizardSheetMappings (
  mappings: BulkImportWizardSheetMapping[]
): BulkImportWizardSheetMapping[] {
  return mappings.map((mapping) => ({
    ...mapping,
    fieldMappings: mapping.fieldMappings.map((fieldMapping: BulkImportWizardColumnMapping) => ({ ...fieldMapping }))
  })) as BulkImportWizardSheetMapping[]
}
